/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaWorkspaceSection.tsx
 * FILE_ID: JOBHUB-QA-WORKSPACE-SECTION-001
 * PURPOSE: Assemble area editing, Build/Run execution, and report generation.
 * LANDMINE: Exactly one breakpoint tree is MOUNTED. Desktop keeps the
 *      toolbar + ITP modal. Phone mounts QaPhoneWorkspace only.
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { QaPhotoPrefsProvider, ResizeHandle, snapshotQaPhotoViews, useIsMdUp, usePersistentBool, usePersistentSize } from '@quotech/ui'
import { QaPositionedMenu } from '../../qa/qaPositionedMenu'
import { useQaWorkspaceContextMenu } from './useQaWorkspaceContextMenu'
import type { QaAreaExecution } from '../../qa/qaApi'
import { normalizeList } from '../../qa/qaApiHelpers'
import { JobQaAreaDetail } from './JobQaAreaDetail'
import { JobQaAreasList } from './JobQaAreasList'
import { JobQaWorkspaceToolbar } from './JobQaWorkspaceToolbar'
import { QaItpExportButton } from './QaItpView'
import { QaItpExportView } from './itp-export'
import { QaItpModal } from './QaItpModal'
import { QaTemplatesDialog } from './templates/QaTemplatesDialog'
import type { QaAreaMode } from '../JobQaModeToggle'
import {
  QA_BUILD_PANE_CLASS,
  QA_PANE_SCROLL_CLASS,
  QA_SPLIT_CLASS,
} from './qaWorkspaceLayout'
import type { JobQaTabController } from './useJobQaTabController'
import { QaPhoneWorkspace } from './phone'
import { flushPendingQuoteDetails, useResolvedItpReportTitle } from './useJobQuoteDetails'
import { useQaWorkspaceCover } from './useQaWorkspaceCover'
import { describeClassification } from '../../qa/itpCover'
import { buildItpRequestHtml } from '../../qa/itpDocument'
import { areasWithEvidenceUrls } from './useQaEvidenceUrls'
import { defaultItpLibraryName } from '../../qa/itpLibraryName'
import { QaItpLibraryNameDialog } from './QaItpLibraryNameDialog'
import { saveItpLibraryEntry } from './saveItpLibraryEntry'
import { useQaItpExecutions } from './useQaItpExecutions'
import { useQaStructureMenu } from './useQaStructureMenu'
import { useQaApplyDrag } from './useQaApplyDrag'
import { QaDragRoot } from '../../qa/useQaPointerDrag'
import { useQaSiblingReorder } from './useQaSiblingReorder'
import { useQaShortcuts } from './useQaShortcuts'
import { useStickyOffset } from './useStickyOffset'
import {
  liveModalRequest,
  resolveItpScope,
  selectedQaUnion,
  type QaItpRequest,
  type QaItpUi,
} from './qaItpScope'

export function JobQaWorkspaceSection({ controller }: { controller: JobQaTabController }) {
  const { api, props, selection, workspace } = controller
  const [renameTarget, setRenameTarget] = useState<{ scope: 'areas' | 'checklists'; id: string } | null>(null)
  const [renameFloorId, setRenameFloorId] = useState<string | null>(null)
  const applyDrag = useQaApplyDrag(controller)
  const menuPoint = useRef({ x: 0, y: 0 })
  const moves = useQaSiblingReorder(controller)
  const structure = useQaStructureMenu({
    api,
    jobId: props.jobId,
    inspectionId: workspace.activeInspectionId,
    setError: controller.setError,
    reorder: workspace.areaMode === 'build' ? moves : undefined,
    onCreated: (scope, id) => setRenameTarget({ scope, id }),
    onRenameLevel: setRenameFloorId,
    onContainedIn: (areaId) => {
      const point = menuPoint.current
      controller.requestContainedIn(areaId, { x: point.x, y: point.y })
    },
  })
  useEffect(() => {
    menuPoint.current = structure.menu.props.position
  }, [structure.menu.props.position])
  const [itpUi, setItpUi] = useState<QaItpUi | null>(null)
  const [exportUi, setExportUi] = useState<{ request: QaItpRequest; showAll: boolean } | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryHtml, setLibraryHtml] = useState('')
  const [libraryRequest, setLibraryRequest] = useState<QaItpRequest | null>(null)
  const [libraryBusy, setLibraryBusy] = useState(false)
  const [libraryError, setLibraryError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const [templatesOpen, setTemplatesOpen] = useState(false)
  useQaShortcuts({
    onOpenTemplates: () => setTemplatesOpen(true),
    onInsertBlank: () => { workspace.addTable.mutate({ seedDefaultItems: false }) },
  })
  const isMdUp = useIsMdUp()
  useQaWorkspaceContextMenu({
    enabled: true,
    wideScreen: isMdUp,
    onArea: ({ id, name, point }) => structure.openArea({ id, area_name: name }, point),
    onChecklist: ({ id, name, areaId, point }) => structure.openChecklist(id, name, areaId, point),
  })
  useEffect(() => {
    if (controller.templatesNonce > 0 && isMdUp) setTemplatesOpen(true)
  }, [controller.templatesNonce, isMdUp])
  const [itpShowAll, setItpShowAll] = useState(false)
  const [areasWidth, setAreasWidth] = usePersistentSize('qa.areas.width', 260)
  // Reused from: packages/ui/src/components/panelResize.tsx (usePersistentBool)
  const [areasCollapsed, setAreasCollapsed] = usePersistentBool('qa.areas.collapsed', false)
  const [checklistsCollapsed, setChecklistsCollapsed] = usePersistentBool('qa.checklists.collapsed', false)
  const [paneMaxH, setPaneMaxH] = usePersistentSize('qa.workspace.paneMaxH', 0)
  const { ref: toolbarRef, height: toolbarH } = useStickyOffset()
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const areaIds = areas.map((area) => area.id)
  const { executions, pending } = useQaItpExecutions(areaIds, api, workspace.areaExecutionsReady)
  const request = resolveItpScope({
    selected: selectedQaUnion(selection.visibleOf, executions, areaIds, selection.excludedChecks),
    activeAreaId: workspace.activeAreaId,
    activeChecklistId: workspace.activeChecklistId,
    allChecklists: workspace.allChecklists,
    executions,
    selectionTouched: selection.touched,
  })
  const itpAreas = executions.filter((area) => request.areaIds.includes(area.id))
  const itpItemKey = request.itemIds?.join(',') ?? ''
  useEffect(() => {
    setItpShowAll(false)
  }, [itpItemKey])
  useEffect(() => {
    setItpUi((prev) => liveModalRequest(prev, request))
  }, [request])
  // Desktop has no All checklists toggle, so a qaAll=1 from the URL or the
  // phone More sheet would otherwise be a hidden, unexitable view.
  const { allChecklists, setAllChecklists } = workspace
  useEffect(() => {
    if (isMdUp && allChecklists) setAllChecklists(false)
  }, [isMdUp, allChecklists, setAllChecklists])
  const reportTitle = useResolvedItpReportTitle(props.jobId)
  const activeInspection = workspace.inspections?.find((row) => row.id === workspace.activeInspectionId)
  const cover = useQaWorkspaceCover(
    props.jobId,
    props.orgId,
    workspace.activeInspectionId,
    describeClassification(activeInspection, props.jobContext),
  )

  if (!workspace.activeInspectionId) {
    if (!templatesOpen) return null
    return (
      <QaTemplatesDialog
        open
        onClose={() => { controller.clearTemplatesInto(); setTemplatesOpen(false) }}
        controller={controller}
      />
    )
  }

  const htmlFor = async (next: QaItpRequest) => {
    const ready = await areasWithEvidenceUrls(queryClient, api, executions)
    return buildItpRequestHtml(
      ready,
      props.jobId,
      next.areaIds,
      next.checklistIds,
      next.itemIds ? new Set(next.itemIds) : undefined,
      reportTitle,
      snapshotQaPhotoViews(),
      cover,
    )
  }
  const beginLibrarySave = (html: string, next: QaItpRequest = request) => {
    if (next.areaIds.length === 0) {
      controller.setError('Select checks, a checklist, or an area first.')
      return
    }
    setLibraryHtml(html)
    setLibraryRequest(next)
    setLibraryError(null)
    setLibraryOpen(true)
  }
  const openExport = (next: QaItpRequest, showAll = false) => {
    void flushPendingQuoteDetails().then(() => setExportUi({ request: next, showAll }))
  }

  const detail = (
    <JobQaAreaDetail
      controller={controller}
      itpRequest={request}
      itpAreas={itpAreas}
      itpPending={pending}
      onItpUi={setItpUi}
      showAll={itpShowAll}
      onShowAll={() => setItpShowAll(true)}
      startRenameChecklistId={renameTarget?.scope === 'checklists' ? renameTarget.id : null}
      onRenameStarted={() => setRenameTarget(null)}
      onOpenChecklistMenu={structure.openChecklist}
      checklistsCollapsed={checklistsCollapsed}
      onToggleChecklists={() => setChecklistsCollapsed(!checklistsCollapsed)}
    />
  )
  const onModeChange = (mode: QaAreaMode) => {
    if (workspace.areaMode === 'build' && (mode === 'run' || mode === 'itp')) {
      void workspace.leaveBuild(mode)
    } else {
      workspace.setAreaMode(mode)
    }
    if (mode === 'itp') {
      setItpUi({ mode: 'modal', ...request })
    }
  }
  const header = (
    <div ref={toolbarRef}>
    <JobQaWorkspaceToolbar
      controller={controller}
      areasWidth={areasWidth}
      areasCollapsed={areasCollapsed}
      onToggleAreas={() => setAreasCollapsed(!areasCollapsed)}
      checklistsCollapsed={checklistsCollapsed}
      onToggleChecklists={() => setChecklistsCollapsed(!checklistsCollapsed)}
      onStartRename={(scope, id) => setRenameTarget({ scope, id })}
      onOpenTemplates={() => {
        controller.consumeSetupWrite?.()
        setTemplatesOpen(true)
      }}
      onModeChange={onModeChange}
      onSaveToLibrary={() => { void htmlFor(request).then((html) => beginLibrarySave(html)) }}
      itpActions={(
        <QaItpExportButton
          onExport={() => openExport(request, itpShowAll)}
          disabled={itpAreas.length === 0}
        />
      )}
    />
    </div>
  )
  const overlays = (
    <>
      {itpUi?.mode === 'modal' && (
        <QaItpModal
          onClose={() => setItpUi(null)}
          areas={executions}
          request={itpUi}
          jobId={props.jobId}
          jobNumber={props.jobId}
          reportTitle={reportTitle}
          cover={cover}
          api={api}
          onMutated={workspace.invalidateExecution}
          onSetStatus={workspace.setItemStatus}
          statusPending={workspace.setStatus.isPending}
          itemSelection={{
            isSelected: (id, checklistId, areaId) => selection.isCheckIncluded(id, checklistId, areaId),
            onToggle: (id, checklistId, areaId) => selection.toggleCheck(id, checklistId, areaId),
          }}
          checklistSelection={{
            isSelected: (id) => selection.isSelected('checklists', id),
            onToggle: (id) => selection.toggle('checklists', id),
          }}
          onExport={(next) => openExport(next, next.itemIds === null)}
          onSaveToLibrary={() => { void htmlFor(request).then((html) => beginLibrarySave(html)) }}
          onWriteError={controller.setError}
          onRenameChecklist={(checklistId, title) => {
            void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
          }}
          onDeleteChecklist={(checklistId) => {
            void api.deleteChecklist(checklistId).then(() => {
              setItpUi(null)
              workspace.invalidateExecution()
            })
          }}
        />
      )}
      <QaTemplatesDialog
        open={templatesOpen}
        onClose={() => { controller.clearTemplatesInto(); setTemplatesOpen(false) }}
        controller={controller}
      />
      {exportUi && workspace.activeInspectionId ? (
        <QaItpExportView
          open
          onClose={() => setExportUi(null)}
          api={api}
          jobId={props.jobId}
          orgId={props.orgId}
          inspectionId={workspace.activeInspectionId}
          executions={executions}
          jobNumber={props.jobId}
          request={exportUi.request}
          defaultShowAll={exportUi.showAll}
          reportTitle={reportTitle}
          cover={cover}
          onSaveToLibrary={(html) => beginLibrarySave(html, exportUi.request)}
        />
      ) : null}
      <QaItpLibraryNameDialog
        open={libraryOpen}
        defaultName={defaultItpLibraryName(itpAreas.map((area) => area.area_name))}
        busy={libraryBusy}
        error={libraryError}
        onCancel={() => { if (!libraryBusy) setLibraryOpen(false) }}
        onSave={(name) => {
          if (!libraryRequest || !workspace.activeInspectionId) return
          setLibraryBusy(true)
          void saveItpLibraryEntry(api, {
            inspectionId: workspace.activeInspectionId,
            name,
            html: libraryHtml,
            request: libraryRequest,
          }).then(() => {
            setLibraryOpen(false)
            void queryClient.invalidateQueries({ queryKey: ['itp-library', props.jobId] })
          }).catch((error: unknown) => {
            setLibraryError(error instanceof Error ? error.message : 'Could not save the ITP.')
          }).finally(() => setLibraryBusy(false))
        }}
      />
    </>
  )

  if (isMdUp) {
    return (
      <QaDragRoot onDrop={applyDrag}>
      <QaPhotoPrefsProvider>
      <div data-qa-workspace>
        {header}
        {overlays}
        {structure.menu.props.open ? (
          <QaPositionedMenu
            x={structure.menu.props.position.x}
            y={structure.menu.props.position.y}
            items={structure.items}
            onClose={structure.menu.props.onClose}
          />
        ) : null}
        <div
          className={QA_SPLIT_CLASS}
          style={{
            gridTemplateColumns: workspace.areaMode !== 'build'
              ? '1fr'
              : areasCollapsed
                ? '0px 0px 1fr'
                : `${areasWidth}px 4px 1fr`,
            ['--qa-rail-top' as string]: `${toolbarH}px`,
            ...(paneMaxH > 0
              ? { ['--qa-pane-max-h' as string]: `${paneMaxH}px` }
              : {}),
          }}
        >
          {workspace.areaMode !== 'build' ? null : areasCollapsed ? (
          <>
          <div />
          <div />
          </>
          ) : (
          <>
          <div className={`${QA_PANE_SCROLL_CLASS} space-y-4`}>
            <JobQaAreasList
              controller={controller}
              openAreaId={workspace.activeAreaId}
              onSelectArea={(areaId) => workspace.selectArea(areaId)}
              startRenameId={renameTarget?.scope === 'areas' ? renameTarget.id : null}
              onRenameStarted={() => setRenameTarget(null)}
              onOpenMenu={structure.openArea}
              onOpenLevelMenu={structure.openLevel}
              renameFloorId={renameFloorId}
              onRenameFloorStarted={() => setRenameFloorId(null)}
            />
          </div>
          <ResizeHandle
            axis="x"
            sign={1}
            value={areasWidth}
            min={180}
            max={560}
            onChange={setAreasWidth}
            resetTo={260}
            ariaLabel="Resize areas column"
          />
          </>
          )}
          <div className={workspace.areaMode === 'build' ? QA_BUILD_PANE_CLASS : QA_PANE_SCROLL_CLASS}>
            {detail}
          </div>
          {/* min matches md:min-h-[28rem] so every drag moves the grid. */}
          <ResizeHandle
            axis="y"
            sign={1}
            value={paneMaxH > 0 ? paneMaxH : Math.round(Math.min(window.innerHeight * 0.7, 640))}
            min={448}
            max={1200}
            onChange={setPaneMaxH}
            resetTo={0}
            ariaLabel="Resize work area height"
            className="col-span-full"
          />
        </div>
      </div>
      </QaPhotoPrefsProvider>
      </QaDragRoot>
    )
  }

  return (
    <QaPhotoPrefsProvider>
      <div data-qa-workspace className="flex h-full min-h-0 flex-col">
        <QaPhoneWorkspace
          controller={controller}
          cover={cover}
          checklistMenuItems={structure.itemsForChecklist}
          renameChecklistId={renameTarget?.scope === 'checklists' ? renameTarget.id : null}
          onRenameChecklistStarted={() => setRenameTarget(null)}
        />
      </div>
    </QaPhotoPrefsProvider>
  )
}
